/* ══════════════════════════════
   utilsJeux.js — shared layout helpers for all game pages
══════════════════════════════ */

/**
 * isLandscapeNow() — reliable orientation check.
 *
 * window.matchMedia('(orientation: landscape)') can lag by one frame
 * after a rotation event. Comparing innerWidth/innerHeight is always
 * current and avoids the timing race in getNavV/getNavH.
 */
function isLandscapeNow() {
  return window.innerWidth > window.innerHeight;
}

/**
 * getNavV() — vertical space consumed by navigation chrome.
 *
 * Mobile portrait  → top header height
 * Mobile landscape → 0  (sidebars are on the sides, not top)
 * Desktop h-nav    → navbar height
 * Desktop sidebar  → 0  (sidebars are fixed overlays, don't push main vertically)
 */
export function getNavV() {
  const root = getComputedStyle(document.documentElement);
  const mobileHeaderH = parseFloat(root.getPropertyValue('--mobile-header-height')) || 0;
  const navbarH = parseFloat(root.getPropertyValue('--navbar-height')) || 0;

  const isMobile = document.body.classList.contains('is-mobile');
  const isHorizontalNav = document.body.classList.contains('horizontal-nav');
  const isLandscape = isLandscapeNow();

  // Mobile landscape: sidebars are left/right — zero vertical chrome
  if (isMobile && isLandscape) return 0;

  return isMobile ? mobileHeaderH : isHorizontalNav ? navbarH : 0;
}

/**
 * getNavH() — horizontal space consumed by navigation chrome.
 *
 * Mobile portrait       → 0  (header is on top)
 * Mobile landscape      → 2 × sidebar width
 * Desktop sidebar nav   → 2 × sidebar width
 * Desktop horizontal nav → 0  (navbar is on top)
 */
export function getNavH() {
  const root = getComputedStyle(document.documentElement);
  const sidebarW = parseFloat(root.getPropertyValue('--sidebar-width')) || 0;

  const isMobile = document.body.classList.contains('is-mobile');
  const isHorizontalNav = document.body.classList.contains('horizontal-nav');
  const isLandscape = isLandscapeNow();

  // Mobile landscape: sidebars on both sides
  if (isMobile && isLandscape) return sidebarW * 2;

  // Desktop sidebar nav: sidebars on both sides
  if (!isMobile && !isHorizontalNav) return sidebarW * 2;

  // Mobile portrait or desktop horizontal nav: no horizontal chrome
  return 0;
}

/* ══════════════════════════════
   shared game sound preference
══════════════════════════════ */

const GAME_SOUND_STORAGE_KEY = 'nl_games_sound_enabled';

export function getGameSoundEnabled() {
  try {
    return localStorage.getItem(GAME_SOUND_STORAGE_KEY) !== '0';
  } catch {
    return true;
  }
}

export function setGameSoundEnabled(enabled) {
  try {
    localStorage.setItem(GAME_SOUND_STORAGE_KEY, enabled ? '1' : '0');
  } catch {
    /* ignore */
  }
}

/* ══════════════════════════════
   achievements.js — shared achievement logic for all game pages
══════════════════════════════ */

const STORAGE_KEY = 'nl_achievements';

// Medal image paths — 1 = gold, 2 = silver, 3 = bronze
const MEDAL_SRCS = {
  1: '/jeux/medal1.webp',
  2: '/jeux/medal2.webp',
  3: '/jeux/medal3.webp'
};

// ── Storage helpers ────────────────────────────────────────────
function loadAchievements() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

function saveAchievements(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

/**
 * Award a medal for a game if it's better than what's already stored.
 * medal: 1 (gold) | 2 (silver) | 3 (bronze) | null (none)
 * Returns the stored best medal (after update).
 */
export function awardMedal(game, medal) {
  const data = loadAchievements();
  const current = data[game] ?? null;
  // Lower number = better medal
  if (medal !== null && (current === null || medal < current)) {
    data[game] = medal;
    saveAchievements(data);
    return medal;
  }
  return current;
}

/**
 * Get the best stored medal for a game.
 */
export function getBestMedal(game) {
  const data = loadAchievements();
  return data[game] ?? null;
}

// ── Medal display next to title ────────────────────────────────

/**
 * Render medal images flanking the .page-title element.
 * Call once on page load (shows best stored medal).
 * Call again after a game ends to reflect a new award.
 */ export function renderTitleMedals(game) {
  const title = document.querySelector('.page-title');
  if (!title) return;

  const medal = getBestMedal(game);
  const existingWrap = document.querySelector('.title-medal-wrap');

  // If no medal, remove wrap if present
  if (!medal) {
    if (existingWrap) existingWrap.replaceWith(title);
    return;
  }

  const src = MEDAL_SRCS[medal];

  // If wrap already exists, just update the src — no DOM restructuring
  if (existingWrap) {
    existingWrap.querySelectorAll('.title-medal').forEach(img => (img.src = src));
    return;
  }

  // First time: build the wrap
  const wrap = document.createElement('div');
  wrap.className = 'title-medal-wrap';

  const makeImg = side => {
    const img = document.createElement('img');
    img.src = src;
    img.alt = `medal ${medal}`;
    img.className = `title-medal title-medal--${side}`;
    return img;
  };

  title.parentNode.insertBefore(wrap, title);
  wrap.appendChild(makeImg('left'));
  wrap.appendChild(title);
  wrap.appendChild(makeImg('right'));
}

// ── End-modal medal message ────────────────────────────────────

const MEDAL_LABELS = {
  1: { emoji: '🥇', key: 'achievementGold' },
  2: { emoji: '🥈', key: 'achievementSilver' },
  3: { emoji: '🥉', key: 'achievementBronze' }
};

/**
 * Build the achievement HTML block for the end modal.
 * earnedMedal: medal awarded this run (null if none).
 * bestMedal:   best medal ever (after this run).
 * t:           langManager.getText function.
 */
export function medalModalHTML(earnedMedal, bestMedal, t) {
  if (!earnedMedal) {
    return `<div class="end-achievement end-achievement--none">${t('achievementNone')}</div>`;
  }

  const { emoji, key } = MEDAL_LABELS[earnedMedal];
  const isNew = earnedMedal === bestMedal;
  const src = MEDAL_SRCS[earnedMedal];

  return `
    <div class="end-achievement end-achievement--medal">
      <img src="${src}" alt="medal" class="end-achievement__img"/>
      <div class="end-achievement__text">
        ${isNew ? `<span class="end-achievement__new">${t('achievementNew')}</span>` : ''}
        ${emoji} ${t(key)}
      </div>
    </div>`;
}

/**
 * #jeux-end-modal modal manager
 * ***************************************************************

 * Owns the single #jeux-end-modal element present in every game page.
 * Exposes:
 *   openJeuxModal()   — show the modal
 *   closeJeuxModal()  — hide the modal
 *
 * Also registers window.closeModal as an alias so that any legacy
 * inline onclick="closeModal()" in HTML still works without changes.
 *
 * Wire-up (call once, early in each game's JS module):
 *   import { initJeuxModal, openJeuxModal, closeJeuxModal } from '../utils/utilsJeux.js';
 *   initJeuxModal();
 *
 * Then instead of:
 *   document.getElementById('end-modal').classList.add('show');
 * use:
 *   openJeuxModal();
 */

const MODAL_ID = 'jeux-end-modal';
const SHOW_CLASS = 'show';

// ── Helpers ────────────────────────────────────────────────────
function _getModal() {
  return document.getElementById(MODAL_ID);
}

// ── Public API ─────────────────────────────────────────────────
export function openJeuxModal() {
  _getModal()?.classList.add(SHOW_CLASS);
}

export function closeJeuxModal() {
  _getModal()?.classList.remove(SHOW_CLASS);
}

// ── Init — call once per page ──────────────────────────────────
let _backdropHandler = null;
let _keydownHandler = null;
let _closeBtn = null;
let _closeBtnHandler = null;

export function initJeuxModal() {
  const modal = _getModal();
  if (!modal) {
    console.warn('[jeuxModal] #jeux-end-modal not found in DOM');
    return;
  }

  // ── Close button inside the modal ─────────────────────────────
  _closeBtn = modal.querySelector('.jeux-modal-close');
  if (_closeBtn) {
    _closeBtnHandler = () => closeJeuxModal();
    _closeBtn.addEventListener('click', _closeBtnHandler);
  }

  // ── Click on the dark backdrop (outside the white card) ───────
  _backdropHandler = e => {
    if (e.target === modal) closeJeuxModal();
  };
  modal.addEventListener('click', _backdropHandler);

  // ── Escape key ────────────────────────────────────────────────
  _keydownHandler = e => {
    if (e.key === 'Escape' && modal.classList.contains(SHOW_CLASS)) {
      closeJeuxModal();
    }
  };
  document.addEventListener('keydown', _keydownHandler);

  // ── Global alias — satisfies inline onclick="closeModal()" ────
  window.closeModal = closeJeuxModal;
}

export function cleanupJeuxModal() {
  const modal = _getModal();

  if (_closeBtnHandler && _closeBtn) {
    _closeBtn.removeEventListener('click', _closeBtnHandler);
    _closeBtnHandler = null;
    _closeBtn = null;
  }
  if (_backdropHandler && modal) {
    modal.removeEventListener('click', _backdropHandler);
    _backdropHandler = null;
  }
  if (_keydownHandler) {
    document.removeEventListener('keydown', _keydownHandler);
    _keydownHandler = null;
  }

  delete window.closeModal;
}
